import { useState, useCallback } from 'react';
import {
    Button,
    Card,
    Center,
    Stack,
    Text,
    Title,
    ThemeIcon,
    Group,
    Alert,
} from '@mantine/core';
import { Icon } from '@iconify/react';
import { useActions, useAppState } from 'src/state';

type RequestStatus = 'idle' | 'loading' | 'success' | 'error';

export default function SendVerifyEmail() {

    const { sendVerificationLinkEmail, signOutAction } = useActions();
    const { profile } = useAppState();
    
    const [status, setStatus] = useState<RequestStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleResendEmail = useCallback(async () => {

        setStatus('loading');
        setErrorMessage(null);
        
        try {
            const success = await sendVerificationLinkEmail();
            if (success) {
                setStatus('success');
            } else {
                setStatus('error');
                setErrorMessage('Failed to send verification email. Please try again.');
            }
        } catch {
            setStatus('error');
            setErrorMessage('An unexpected error occurred. Please try again.');
        }
        
    }, [sendVerificationLinkEmail]);

    const handleTryAgain = useCallback(() => {
        setStatus('idle');
        setErrorMessage(null);
    }, []);

    return (
        <Center pt="+100" p="md">
            <Card withBorder shadow="md" p="xl" radius="md" w={450}>
                <Stack gap="lg" align="center">
                    {/* Icon */}
                    <ThemeIcon
                        size={80}
                        radius="xl"
                        variant="light"
                        color={status === 'success' ? 'green' : status === 'error' ? 'red' : 'blue'}
                    >
                        <Icon
                            icon={
                                status === 'success'
                                    ? 'mdi:check-circle'
                                    : status === 'error'
                                      ? 'mdi:alert-circle'
                                      : 'mdi:email-outline'
                            }
                            width={40}
                            height={40}
                        />
                    </ThemeIcon>

                    {/* Title */}
                    <Title order={2} ta="center">
                        {status === 'success'
                            ? 'Email Sent!'
                            : status === 'error'
                              ? 'Something Went Wrong'
                              : 'Verify Your Email'}
                    </Title>

                    {/* Description */}
                    <Text c="dimmed" ta="center" size="sm">
                        {status === 'success' ? (
                            <>
                                We've sent a new verification link to your email.
                                <br />
                                Please check your inbox and spam folder.
                            </>
                        ) : status === 'error' ? (
                            errorMessage
                        ) : profile?.email ? (
                            <>
                                A verification link has been sent to
                                <Text span fw={600} c="blue">
                                    {' '}{profile.email}
                                </Text>
                                . Please check your inbox to verify your account.
                            </>
                        ) : (
                            <>
                                A verification link has been sent to your email address.
                                <br />
                                Please check your inbox to verify your account.
                            </>
                        )}
                    </Text>

                    {/* Error Alert */}
                    {status === 'error' && (
                        <Alert
                            color="red"
                            variant="light"
                            w="100%"
                            icon={<Icon icon="mdi:alert" width={18} />}
                        >
                            Unable to send verification email. This could be due to rate limiting or a server issue.
                        </Alert>
                    )}

                    {/* Actions */}
                    <Stack gap="sm" w="100%" mt="md">
                        {status === 'success' ? (
                            <Button
                                fullWidth
                                variant="light"
                                leftSection={<Icon icon="mdi:refresh" width={18} />}
                                onClick={handleTryAgain}
                            >
                                Send Another Email
                            </Button>
                        ) : status === 'error' ? (
                            <Button
                                fullWidth
                                leftSection={<Icon icon="mdi:refresh" width={18} />}
                                onClick={handleResendEmail}
                            >
                                Try Again
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                loading={status === 'loading'}
                                leftSection={status !== 'loading' ? <Icon icon="mdi:email-fast" width={18} /> : undefined}
                                onClick={handleResendEmail}
                            >
                                Resend Verification Email
                            </Button>
                        )}
                    </Stack>

                    {/* Help Text */}
                    <Group gap="xs" mt="sm">
                        <Text size="xs" c="dimmed">
                            Didn't receive the email?
                        </Text>
                        <Text
                            size="xs"
                            c="blue"
                            component="a"
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                handleResendEmail();
                            }}
                            style={{ cursor: status === 'loading' ? 'not-allowed' : 'pointer' }}
                        >
                            Click here to resend
                        </Text>
                    </Group>

                    {/* Sign Out Option */}
                    <Button
                        variant="subtle"
                        color="gray"
                        size="xs"
                        onClick={signOutAction}
                        leftSection={<Icon icon="mdi:logout" width={14} />}
                    >
                        Sign out and use a different account
                    </Button>
                </Stack>
            </Card>
        </Center>
    );
}
